import { Button, Icon } from '@economic/taco';
import { useCurrentAgreement, useCurrentUser, useLocalStorage } from '@economic/react-app';
import { t, Trans } from '@lingui/macro';
import React from 'react';

import { EsignatureOnboardingStatus } from '../../../../../../types/internal-api';
import { Field } from '../../../../../shared/components/Form';
import useAppSettings from '../../../../../shared/hooks/useAppSettings';
import track from '../../../../../shared/util/utilities/mixpanel';
import { isESignatureTopNavbarIconHidden } from '../../../../e-signature/util';

type Props = {
    esignatureOnboardingStatus?: EsignatureOnboardingStatus;
    onSigningTemplateChange: (signingTemplateId: string) => void;
};

export function SendOrderESignatureSettings(props: Props) {
    const user = useCurrentUser();
    const agreement = useCurrentAgreement();
    const { esignatureOnboardingStatus, onSigningTemplateChange } = props;

    const {
        data: eSignatureSendOrderVisibilityData,
        loading: loadingESignatureSendOrderVisibility,
        set: setESignatureSendOrderVisibility,
    } = useAppSettings('isEsignatureSendOrderHidden');

    const eSignatureNavbarIconVisibilitySettings = useAppSettings('isESignatureNavbarIconHidden');
    const [isESignatureNavbarIconHiddenLocalStorage] = useLocalStorage<boolean>('isESignatureNavbarIconHidden');
    const [isESignatureWidgetDismissed] = useLocalStorage<boolean>('isESignatureWidgetDismissed');

    //if user disabled ESignatureWidget in the home dashbord or hidden ESignature Icon in top navigation we should return null
    const hideESignatureSettings =
        !props.esignatureOnboardingStatus &&
        (isESignatureWidgetDismissed ||
            isESignatureTopNavbarIconHidden(
                user,
                isESignatureNavbarIconHiddenLocalStorage,
                eSignatureNavbarIconVisibilitySettings
            ));

    const handleHideESignatureSettings = visibility => {
        onSigningTemplateChange('NOSIGNINGREQUIRED');
        setESignatureSendOrderVisibility({
            ...eSignatureSendOrderVisibilityData?.content,
            isEsignatureSendOrderHidden: visibility,
        });
    };

    const sectionContentHidden =
        loadingESignatureSendOrderVisibility || eSignatureSendOrderVisibilityData?.content?.isEsignatureSendOrderHidden;

    if (!agreement.subscription.isInvoiced || hideESignatureSettings) {
        return null;
    }
    return (
        <div className="flex flex-col w-full mt-4 items-start">
            <span className="font-bold text-xs mb-1">
                <Trans>Do you want your order signed?</Trans>
            </span>
            {sectionContentHidden ? null : !esignatureOnboardingStatus ? (
                <div className="flex">
                    <div className="mt-1">
                        <Icon name="info" className="rounded-full yt-blue-solid text-white mr-2" />
                    </div>
                    <div>
                        <Trans>
                            Activate Digital Signatures to get access to this feature. Read more about Digital Signatures in{' '}
                            <a
                                href={'https://www.e-conomic.dk/support/artikler/kom-godt-i-gang-med-digitale-underskrifter'}
                                onClick={() => track('esignature', 'clicked.readmore', { clickedFrom: 'sendorder' })}
                                target="_blank">
                                e-copedia
                            </a>
                        </Trans>
                    </div>
                </div>
            ) : (
                <div className="flex flex-none w-full">
                    <Field.Dropdown
                        name="orderSendESignatureRequest.signingTemplateId"
                        fieldClassName="w-8/12 -mb-8"
                        onChange={value => onSigningTemplateChange(value)}
                        options={esignatureOnboardingStatus?.signingTemplates.map(template => ({
                            value: template.key,
                            text: template.value,
                        }))}
                    />
                    <div className="ml-2 mt-1">
                        {esignatureOnboardingStatus.freeSigningsLeft > 0
                            ? t`Free`
                            : esignatureOnboardingStatus.priceLine + ' ' + t`kr ex. VAT`}
                    </div>
                </div>
            )}
            <Button
                className={`p-0 ${esignatureOnboardingStatus === undefined ? 'mt-2' : ''}`}
                appearance="discrete"
                onClick={() => handleHideESignatureSettings(!sectionContentHidden)}>
                {sectionContentHidden ? (
                    <Trans>Show digital signatures options</Trans>
                ) : (
                    <Trans>Hide digital signatures options</Trans>
                )}
            </Button>
        </div>
    );
}
